import L from 'leaflet'
import { FC } from 'react'
import { Marker, Tooltip } from 'react-leaflet'
import * as turf from '@turf/turf'
import type { Feature, Polygon, MultiPolygon } from 'geojson'
import { useMapContext } from './MapContext'

interface MultiPolygonDeleteMarkersProps {
  feature: Feature<Polygon | MultiPolygon>
  onDeletePolygon: (updatedFeature: Feature<Polygon | MultiPolygon>) => void
  getTooltipText?: (polygonIndex: number) => string
  iconHtml?: string
  iconSize?: number
}

export const removePolygonFromMultiPolygon = <P extends Record<string, unknown>>(
  feature: Feature<MultiPolygon, P>,
  polygonIndex: number,
): Feature<Polygon | MultiPolygon, P> | null => {
  if (feature.geometry.type !== 'MultiPolygon') return null

  const coords = feature.geometry.coordinates
  if (coords.length <= 1) return null // Don't allow deleting last polygon

  const newCoords = coords.filter((_, idx) => idx !== polygonIndex)

  if (newCoords.length === 1) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: newCoords[0],
      } as Polygon,
      properties: { ...feature.properties },
    }
  }
  return {
    type: 'Feature',
    geometry: {
      type: 'MultiPolygon',
      coordinates: newCoords,
    } as MultiPolygon,
    properties: { ...feature.properties },
  }
}

const DEFAULT_ICON_HTML = [
  "<div style='background: white; border-radius: 50%; box-shadow: 0 0 2px #000; padding: 2px; display: flex; align-items: center; justify-content: center; width: 28px; height: 28px;'>",
  "<svg width='20' height='20' fill='red' viewBox='0 0 24 24'><path d='M6 19c0 1.104.896 2 2 2h8c1.104 0 2-.896 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z'/></svg>",
  '</div>',
].join('')

const DEFAULT_TOOLTIP_TEXT = (index: number) => `Eliminar polígono ${index + 1}`

export const MultiPolygonDeleteMarkers: FC<MultiPolygonDeleteMarkersProps> = ({
  feature,
  onDeletePolygon,
  getTooltipText = DEFAULT_TOOLTIP_TEXT,
  iconHtml = DEFAULT_ICON_HTML,
  iconSize = 28,
}) => {
  const { showDeletePolygons } = useMapContext()

  if (!showDeletePolygons || !feature || feature.geometry.type !== 'MultiPolygon') {
    return null
  }

  const featureId = feature.properties?.id || feature.properties?.tmpId || 'feature'

  const handleDeletePolygon = (polygonIndex: number) => {
    const updatedFeature = removePolygonFromMultiPolygon(
      feature as Feature<MultiPolygon>,
      polygonIndex,
    )
    if (updatedFeature) {
      onDeletePolygon(updatedFeature)
    }
  }

  return (
    <>
      {feature.geometry.coordinates.map((polygon, idx) => {
        const centerOfPolygon = turf.center(turf.polygon(polygon)).geometry.coordinates
        const markerKey = `delete-poly-${featureId}-${idx}`

        return (
          <Marker
            key={markerKey}
            eventHandlers={{
              click: () => handleDeletePolygon(idx),
            }}
            icon={L.divIcon({
              className: 'delete-polygon-marker',
              html: iconHtml,
              iconSize: [iconSize, iconSize],
              iconAnchor: [iconSize / 2, iconSize / 2],
            })}
            position={[centerOfPolygon[1], centerOfPolygon[0]]}
          >
            <Tooltip permanent direction="top" offset={[0, -10]} opacity={1}>
              {getTooltipText(idx)}
            </Tooltip>
          </Marker>
        )
      })}
    </>
  )
}
